import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ship, Shield, CheckCircle, Anchor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, whyChooseUs, companyInfo } from '../data/mock';

const Home = () => {
  const featuredServices = services.slice(0, 6);
  const topFeatures = whyChooseUs.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/hero-ships.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-maritime-primary/85"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-maritime-accent/20 backdrop-blur-sm border border-maritime-accent/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-maritime-accent" />
              <span className="text-sm text-white font-medium">
                Approved by Maritime New Zealand
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Navigating Excellence in Marine Survey
            </h1>
            
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Professional marine inspection and surveying services across Australia, 
              New Zealand, Pacific Islands, and India. Delivering accurate, independent, 
              and reliable solutions to the global maritime industry.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-maritime-accent hover:bg-maritime-accent/90 text-white w-full sm:w-auto"
                >
                  Request a Survey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-maritime-primary w-full sm:w-auto"
                >
                  Our Services
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-white/20">
              <div>
                <div className="text-3xl font-bold text-white mb-1">25+</div>
                <div className="text-sm text-white/90">Years Experience</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">5000+</div>
                <div className="text-sm text-white/90">Surveys Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">4</div>
                <div className="text-sm text-white/90">Countries Served</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-maritime-primary/5 rounded-full px-4 py-2 mb-4">
              <Ship className="w-4 h-4 text-maritime-primary" />
              <span className="text-sm text-maritime-primary font-medium">Our Services</span>
            </div>
            <h2 className="text-4xl font-bold text-maritime-primary mb-4">
              Comprehensive Marine Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From vessel inspections to cargo surveys, we provide a complete range 
              of marine inspection and technical services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {featuredServices.map((service) => {
              const IconComponent = require('lucide-react')[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-maritime-accent/20 overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-maritime-primary/60 group-hover:bg-maritime-primary/40 transition-colors duration-300"></div>
                    <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg">
                      <IconComponent className="w-6 h-6 text-maritime-accent" />
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-maritime-primary mb-2 group-hover:text-maritime-accent transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-2 border-maritime-primary text-maritime-primary hover:bg-maritime-primary hover:text-white"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-maritime-primary/5 to-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-maritime-accent/10 rounded-full px-4 py-2 mb-4">
                <CheckCircle className="w-4 h-4 text-maritime-accent" />
                <span className="text-sm text-maritime-primary font-medium">Why Choose Us</span>
              </div>
              <h2 className="text-4xl font-bold text-maritime-primary mb-6">
                Trusted Expertise in Marine Surveying
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                As an approved National Inspection Agency by Maritime New Zealand, 
                we bring unparalleled expertise, reliability, and compliance to every project.
              </p>

              <div className="space-y-6">
                {topFeatures.map((feature) => {
                  const IconComponent = require('lucide-react')[feature.icon];
                  return (
                    <div key={feature.id} className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-maritime-accent/10 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-maritime-accent" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-maritime-primary mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Link to="/why-us">
                <Button 
                  className="mt-8 bg-maritime-accent hover:bg-maritime-accent/90 text-white"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1645932647969-6a92e3d6667f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxzaGlwJTIwaW5zcGVjdGlvbnxlbnwwfHx8fDE3NzEyMDIxNDB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Marine Professionals at Work"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-4 border-maritime-accent/20">
                <div className="flex items-center gap-4">
                  <div className="bg-maritime-accent p-3 rounded-lg">
                    <Anchor className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-maritime-primary">100%</div>
                    <div className="text-sm text-gray-600">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-maritime-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1623944156546-ddd5bf10796e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxwb3J0JTIwb3BlcmF0aW9uc3xlbnwwfHx8fDE3NzEyMDIyMjV8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contact our experienced team for professional marine survey services. 
              Quick response and reliable reporting guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-maritime-accent hover:bg-maritime-accent/90 text-white w-full sm:w-auto"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-maritime-primary w-full sm:w-auto"
                >
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
